import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { Schemas } from '@npc/api-client'

import { api } from '../../app/api'
import { call } from '../../app/errors'

export type SystemModelModuleView = Schemas['SystemModelModuleView']
export type SystemModelModuleKey = Schemas['SystemModelModuleKey']

export function useSystemModels(projectId?: string) {
  return useQuery({
    queryKey: ['system-models', projectId ?? null],
    queryFn: () =>
      call('/system-models/modules', () =>
        api.GET('/system-models/modules', {
          params: { query: projectId ? { project_id: projectId } : {} },
        }),
      ),
  })
}

function useInvalidateSystemModels() {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: ['system-models'] })
    void client.invalidateQueries({ queryKey: ['lulu-agents'] })
  }
}

export function useSetSystemModel() {
  const invalidate = useInvalidateSystemModels()
  return useMutation({
    mutationFn: ({ moduleKey, profileId, projectId }: { moduleKey: SystemModelModuleKey; profileId: string; projectId?: string }) =>
      projectId
        ? call('/projects/{projectId}/system-models/modules/{moduleKey}', () =>
            api.PUT('/projects/{projectId}/system-models/modules/{moduleKey}', {
              params: { path: { projectId, moduleKey } },
              body: { profile_id: profileId },
            }),
          )
        : call('/system-models/modules/{moduleKey}', () =>
            api.PUT('/system-models/modules/{moduleKey}', {
              params: { path: { moduleKey } },
              body: { profile_id: profileId },
            }),
          ),
    onSuccess: invalidate,
  })
}

export function usePauseSystemModel() {
  const invalidate = useInvalidateSystemModels()
  return useMutation({
    mutationFn: ({ moduleKey, projectId }: { moduleKey: SystemModelModuleKey; projectId?: string }) =>
      projectId
        ? call('/projects/{projectId}/system-models/modules/{moduleKey}/pause', () =>
            api.POST('/projects/{projectId}/system-models/modules/{moduleKey}/pause', {
              params: { path: { projectId, moduleKey } },
            }),
          )
        : call('/system-models/modules/{moduleKey}/pause', () =>
            api.POST('/system-models/modules/{moduleKey}/pause', { params: { path: { moduleKey } } }),
          ),
    onSuccess: invalidate,
  })
}

export function useDeleteSystemModelOverride() {
  const invalidate = useInvalidateSystemModels()
  return useMutation({
    mutationFn: ({ moduleKey, projectId }: { moduleKey: SystemModelModuleKey; projectId: string }) =>
      call('/projects/{projectId}/system-models/modules/{moduleKey}', () =>
        api.DELETE('/projects/{projectId}/system-models/modules/{moduleKey}', {
          params: { path: { projectId, moduleKey } },
        }),
      ),
    onSuccess: invalidate,
  })
}
